import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Device } from '@capacitor/device';
import { auth } from './config';

export class FirebaseAuthService {
  private isNative: boolean | null = null;

  private async checkPlatform(): Promise<boolean> {
    if (this.isNative === null) {
      try {
        const info = await Device.getInfo();
        this.isNative = info.platform === 'ios' || info.platform === 'android';
      } catch {
        this.isNative = false;
      }
    }
    return this.isNative;
  }

  async signIn(email: string, password: string): Promise<string> {
    const isNative = await this.checkPlatform();

    if (isNative) {
      const result = await FirebaseAuthentication.signInWithEmailAndPassword({
        email,
        password,
      });
      const { token } = await FirebaseAuthentication.getIdToken();
      return token;
    } else {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user.getIdToken();
    }
  }

  async register(email: string, password: string): Promise<string> {
    const isNative = await this.checkPlatform();

    if (isNative) {
      await FirebaseAuthentication.createUserWithEmailAndPassword({
        email,
        password,
      });
      const { token } = await FirebaseAuthentication.getIdToken();
      return token;
    } else {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user.getIdToken();
    }
  }

  async getIdToken(): Promise<string | null> {
    const isNative = await this.checkPlatform();

    if (isNative) {
      try {
        const { token } = await FirebaseAuthentication.getIdToken();
        return token;
      } catch {
        return null;
      }
    } else {
      const user = auth.currentUser;
      return user ? await user.getIdToken() : null;
    }
  }

  async logout(): Promise<void> {
    const isNative = await this.checkPlatform();

    if (isNative) {
      await FirebaseAuthentication.signOut();
    } else {
      await signOut(auth);
    }
  }

  async getCurrentUser(): Promise<FirebaseUser | null> {
    const isNative = await this.checkPlatform();

    if (isNative) {
      try {
        const result = await FirebaseAuthentication.getCurrentUser();
        return result.user as unknown as FirebaseUser;
      } catch {
        return null;
      }
    } else {
      return auth.currentUser;
    }
  }

  // Método auxiliar para suscribirse a cambios en el estado de autenticación
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): () => void {
    const setupAuthStateListener = async () => {
      const isNative = await this.checkPlatform();

      if (isNative) {
        // Configurar listener nativo
        FirebaseAuthentication.addListener('authStateChanged', (change) => {
          callback(change.user as unknown as FirebaseUser);
        });
        
        // Retornar función de limpieza
        return () => {
          FirebaseAuthentication.removeAllListeners();
        };
      } else {
        // Configurar listener web
        return auth.onAuthStateChanged(callback);
      }
    };

    let cleanup: (() => void) | undefined;
    setupAuthStateListener().then(cleanupFn => {
      cleanup = cleanupFn;
    });

    // Retornar función de limpieza
    return () => {
      if (cleanup) cleanup();
    };
  }
}

export const firebaseAuth = new FirebaseAuthService();